import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { TutorialProgressService } from './tutorial-progress.service';
import { TUTORIALS } from './tutorials.registry';
import {
  Tutorial,
  TourViewState,
  TutorialStatus,
  TutorialStep,
  WorkspaceGuard,
} from './tutorial.types';

/**
 * Motor / máquina de estados del sistema de tutoriales guiados. Portado de
 * Skuld OSINT (SCG-368).
 *
 * Responsable de: tutorial activo y paso actual, navegación
 * (next/prev/pause/finish), invitación por ruta, filtrado por audiencia y por
 * permisos de paso, espera de anclas que montan tarde, y ejecución de
 * `hostAction` (acciones gratis y reversibles que exponen los componentes host).
 */
@Injectable({ providedIn: 'root' })
export class GuidedTourService {
  /** Estado que consume el overlay. null ⇒ no hay tutorial activo. */
  private readonly view$ = new BehaviorSubject<TourViewState | null>(null);

  private active: Tutorial | null = null;
  /** Pasos visibles (tras filtrar por permisos) del tutorial activo. */
  private steps: TutorialStep[] = [];
  private index = 0;

  /** Acciones gratis/reversibles registradas por componentes host. */
  private readonly hostActions = new Map<string, () => void | Promise<void>>();

  /**
   * Tutorial OFRECIDO (no lanzado) al overlay, que lo pinta como la píldora de
   * invitación (§20 T1). Es el único canal por el que el asistente puede
   * aparecer sin que el usuario lo haya pedido — y siempre como una oferta
   * descartable, nunca como un recorrido encima del trabajo. `null` = nada que
   * ofrecer / invitación consumida.
   */
  private readonly offer$ = new BehaviorSubject<string | null>(null);

  /**
   * Guardianes del espacio de trabajo (§20 T6.3). Los registran los hosts que
   * el tutorial puede mutar (pestaña del caso, drawers del grafo, panel del
   * chat) para poder devolver la vista como estaba al salir.
   */
  private readonly workspaceGuards = new Map<string, WorkspaceGuard>();
  private readonly workspaceSnapshots = new Map<string, unknown>();
  /**
   * ¿El tutorial llegó a mover la vista del usuario? Solo se restaura si sí:
   * si el recorrido no abrió ninguna pestaña/drawer, salir no debe tocar nada.
   */
  private workspaceTouched = false;

  /**
   * ¿El paso actual está "gated" (§17.9) esperando que el usuario realice la
   * acción real? El overlay lo consulta para ocultar "Siguiente" y mostrar
   * el hint. Se limpia al entrar a cualquier paso y se marca `true` de nuevo
   * si el nuevo paso trae `confirmAction`.
   */
  private awaitingConfirm = false;

  /**
   * Vigilancia de "knockout" (§19): si el ancla del paso actual DESAPARECE del
   * DOM (p. ej. el usuario clicó fuera y se cerró el panel de búsqueda), se
   * pausa el tutorial recordando el paso en vez de dejar la tarjeta apuntando a
   * la nada. Se tolera 1 tick por re-render transitorio; al 2º se pausa.
   */
  private knockoutTimer: ReturnType<typeof setInterval> | null = null;
  private watchedTarget: HTMLElement | null = null;
  private knockoutMisses = 0;

  constructor(
    private router: Router,
    private progress: TutorialProgressService,
  ) {}

  // ── API pública ────────────────────────────────────────────────────────────

  observe(): Observable<TourViewState | null> {
    return this.view$.asObservable();
  }

  /** Stream del estado local (pausados + interruptor global) para el FAB. */
  observeLocal(): Observable<unknown> {
    return this.progress.observeLocal();
  }

  /**
   * Stream del mapa de progreso (notDone/done/skipped). Lo consumen los chips
   * de "¿Cómo funciona esto?" (§20 T2): al completar un recorrido de una cadena
   * el chip debe pasar a ofrecer el siguiente sin recargar la pantalla.
   */
  observeProgress(): Observable<unknown> {
    return this.progress.observe();
  }

  // ── Interruptor MAESTRO de tutoriales (admin, toda la organización, §19.11) ──

  /** ¿Los tutoriales están ENCENDIDOS a nivel organización (admin)? */
  isAdminEnabled(): boolean {
    return this.progress.isAdminEnabled();
  }

  observeAdminEnabled(): Observable<boolean> {
    return this.progress.observeAdminEnabled();
  }

  /**
   * ¿La sesión puede tocar el interruptor de organización? (rol con
   * `editRoles`; en OSINT era el superAdmin).
   */
  canManageOrgTutorials(): boolean {
    return this.progress.canManageOrgTutorials();
  }

  /** SOLO rol con editRoles: enciende/apaga los tutoriales para toda la organización. */
  setAdminEnabled(enabled: boolean): Observable<boolean> {
    if (!enabled) this.stop(); // apagar cierra cualquier tour activo
    return this.progress.setAdminEnabled(enabled);
  }

  /** Re-sincroniza el flag maestro desde el backend. */
  refreshAdminEnabled(): Observable<boolean> {
    return this.progress.fetchAdminEnabled();
  }

  /**
   * Logout: cierra el tour activo y vacía el estado en memoria, para que el
   * siguiente usuario de la misma pestaña no vea (ni suba a su cuenta) el
   * progreso del anterior.
   */
  resetSession(): void {
    this.stop();
    this.offer$.next(null);
    this.progress.reset();
  }

  /**
   * Re-sincroniza desde el backend (`GET /user/me/tutorials`) el MAPA DE
   * PROGRESO y el estado del asistente (pausados, interruptor por-usuario,
   * enfriamientos). Lo llama el overlay una vez por sesión: es la única
   * hidratación: el cliente ya no guarda nada en el navegador.
   */
  refreshProgress(): Observable<Record<string, TutorialStatus>> {
    return this.progress.refreshFromServer();
  }

  /**
   * ¿Sabemos de verdad qué vio el usuario? `false` mientras el GET de
   * hidratación no haya llegado bien. Sin esto, un GET fallido deja el mapa
   * vacío y "no lo sabemos" pasa por "no lo ha visto": el recorrido ya
   * completado se volvía a ofrecer en cada login.
   */
  isProgressHydrated(): boolean {
    return this.progress.isHydrated();
  }

  /**
   * ¿El asistente está apagado por CUALQUIER vía? El admin (org) manda sobre la
   * preferencia por-usuario: si el admin apaga, nadie ve tutoriales.
   */
  private isEffectivelyOff(): boolean {
    return !this.progress.isAdminEnabled() || this.progress.isGloballyDisabled();
  }

  /**
   * ¿Los tutoriales están apagados por CUALQUIER interruptor (admin org o
   * preferencia del usuario)? Público para que la UI (perfil) desactive los
   * botones de "Iniciar/Retomar" cuando no deben poder lanzar nada.
   */
  areTutorialsOff(): boolean {
    return this.isEffectivelyOff();
  }

  isActive(): boolean {
    return this.active !== null;
  }

  getById(id: string): Tutorial | undefined {
    return TUTORIALS.find((t) => t.id === id);
  }

  getAll(): Tutorial[] {
    return TUTORIALS.filter((t) => !this.noAplicaAlUsuario(t));
  }

  /**
   * ¿Este recorrido queda fuera para QUIEN ES el usuario (no para lo que ya
   * hizo)? Evalúa la `audiencia` del tutorial contra la sesión viva. Sin
   * usuario, o si el predicado revienta (rol sin poblar), queda fuera: mejor no
   * ofrecer que ofrecer algo que no le toca.
   */
  private noAplicaAlUsuario(t: Tutorial | undefined): boolean {
    if (!t?.audiencia) return false;
    const u = this.progress.currentUser();
    if (!u) return true;
    try {
      return !t.audiencia(u);
    } catch {
      return true;
    }
  }

  /**
   * Tutorial a OFRECER al entrar a `path`. Recorre TODOS los tutoriales cuyo
   * `routeMatch` coincide, en el orden del registro, y devuelve el primero que
   * se pueda ofrecer. En OSINT se tomaba solo el PRIMERO que coincidía: si ese
   * ya estaba completado o no era para el usuario, los demás de la misma ruta
   * ('/dashboard' cubre '/dashboard-alerts') no se ofrecían nunca.
   */
  findOffer(path: string): Tutorial | null {
    for (const t of TUTORIALS) {
      if (t.autoStart === false || !t.routeMatch) continue;
      const coincide =
        typeof t.routeMatch === 'string'
          ? path.includes(t.routeMatch)
          : t.routeMatch.test(path);
      if (!coincide) continue;
      // Pestaña que la unidad no tiene (p. ej. Immersion): no hay nada que
      // mostrar, aunque la ruta coincida.
      if (
        t.requiresAnchor &&
        !document.querySelector(`[data-tour="${t.requiresAnchor}"]`)
      ) {
        continue;
      }
      if (this.shouldAutoStart(t.id) && this.canOffer(t.id)) return t;
    }
    return null;
  }

  /**
   * ¿Debe auto-lanzarse este tutorial? true solo si autoStart !== false y el
   * usuario no lo ha visto (notDone). skipped/done cuentan como "ya visto".
   */
  shouldAutoStart(id: string): boolean {
    const t = this.getById(id);
    if (!t || t.autoStart === false) return false;
    if (this.noAplicaAlUsuario(t)) return false;
    // Apagado (admin org o preferencia del usuario), o el tutorial quedó PAUSADO:
    // no se re-abre solo — el FAB de retomar se encarga de ofrecerlo.
    if (this.isEffectivelyOff()) return false;
    if (this.progress.isPaused(id)) return false;
    return this.progress.getStatus(id) === 'notDone';
  }

  /**
   * ¿El usuario aún no ha visto (ni saltado) este tutorial? A diferencia de
   * `shouldAutoStart`, NO exige `autoStart !== false`: es para disparadores
   * MANUALES que no dependen de una ruta (p. ej. abrir la tab Inmersión lanza
   * `investigacion.grafo`, que es `autoStart:false` porque vive en una tab, no
   * en una URL — §5.2/§11). Con `shouldAutoStart` ese disparo nunca se cumplía.
   */
  isPending(id: string): boolean {
    const t = this.getById(id);
    if (!t) return false;
    if (this.noAplicaAlUsuario(t)) return false;
    // Igual que `shouldAutoStart`: si está apagado (admin/usuario) o pausado, los
    // disparos por tab/modal NO deben relanzarlo — lo retoma el FAB.
    if (this.isEffectivelyOff()) return false;
    if (this.progress.isPaused(id)) return false;
    return this.progress.getStatus(id) === 'notDone';
  }

  // ── Activar/desactivar (Centro de Tutoriales en Perfil, §19) ────────────────

  /** ¿El asistente de tutoriales está apagado por completo? (interruptor global) */
  isGloballyDisabled(): boolean {
    return this.progress.isGloballyDisabled();
  }

  /** Enciende/apaga TODO el asistente. Al apagar, cierra el tour activo. */
  setGloballyDisabled(off: boolean): void {
    this.progress.setGloballyDisabled(off);
    if (off) this.stop();
  }

  /** ¿Un tutorial concreto está desactivado? (reusa el estado `skipped`.) */
  isDisabled(id: string): boolean {
    return this.progress.getStatus(id) === 'skipped';
  }

  /** Desactiva un tutorial (no se auto-lanza ni ofrece retomar). */
  disableTutorial(id: string): void {
    this.progress.clearPaused(id);
    this.progress.setStatus(id, 'skipped').subscribe();
    if (this.active?.id === id) this.stop();
  }

  /** Reactiva un tutorial desactivado: vuelve a `notDone`. */
  enableTutorial(id: string): void {
    this.resetTutorial(id);
  }

  /**
   * Tutorial encadenado que se puede OFRECER al terminar el activo (§20 T5).
   * Antes `finish()` arrancaba la cadena sola y el botón primario del último
   * paso decía "Continuar con…", así que el gesto natural de cerrar metía al
   * usuario en otros 22 pasos. Ahora el encadenado es un botón SECUNDARIO y el
   * primario es "Finalizar". Devuelve null si no hay cadena, si los tutoriales
   * están apagados o si el usuario ya descartó el tutorial encadenado.
   */
  getChainOffer(): { id: string; label: string } | null {
    const t = this.active;
    if (!t?.chainTo) return null;
    if (this.isEffectivelyOff()) return null;
    const next = this.getById(t.chainTo);
    if (!next) return null;
    if (this.isDisabled(next.id)) return null; // "no mostrar más"
    return { id: next.id, label: t.chainLabel || `Continuar con ${next.title}` };
  }

  /**
   * "No, gracias" / "No mostrar más" (§20 T1 y T4): el usuario descarta el
   * tutorial y no se le vuelve a ofrecer — ni invitación, ni FAB de retomar.
   * Reusa el estado `skipped`, así que sigue reactivable desde el Centro de
   * Tutoriales del perfil. Es el "no" que antes no existía: salir solo pausaba.
   */
  dismiss(id: string): void {
    this.disableTutorial(id);
  }

  /** ¿Este tutorial quedó pausado a medias? (para mostrar "Retomar".) */
  isPaused(id: string): boolean {
    return this.progress.isPaused(id);
  }

  /** Registra una host-action gratis/reversible (la llama un componente host). */
  registerHostAction(name: string, fn: () => void | Promise<void>): void {
    this.hostActions.set(name, fn);
  }

  unregisterHostAction(name: string): void {
    this.hostActions.delete(name);
  }

  // ── Invitación (ofrecer ≠ lanzar, §20 T1) ───────────────────────────────────

  /** Stream de tutoriales OFRECIDOS. Lo consume el overlay (píldora). */
  observeOffers(): Observable<string | null> {
    return this.offer$.asObservable();
  }

  /**
   * ¿Se puede OFRECER este tutorial ahora? Pendiente (ni visto ni descartado ni
   * apagado ni pausado) y fuera del enfriamiento de invitaciones (§20 T6.4).
   */
  canOffer(id: string): boolean {
    if (this.isActive()) return false;
    if (!this.isPending(id)) return false;
    return !this.progress.wasOfferedRecently(id);
  }

  /**
   * Pide al overlay que OFREZCA un tutorial con la píldora discreta. Sustituye
   * a los `start()` automáticos (§20 T1/T2): abrir la tab de Inmersión o
   * terminar un recorrido encadenado ofrece el siguiente, no lo impone.
   */
  offer(id: string): void {
    if (!this.canOffer(id)) return;
    this.offer$.next(id);
  }

  /** El overlay consumió la invitación (se aceptó, descartó o aplazó). */
  consumeOffer(id: string): void {
    this.progress.markOffered(id);
    if (this.offer$.value === id) this.offer$.next(null);
  }

  // ── Espacio de trabajo (no dejar la vista movida, §20 T6.3) ─────────────────

  /**
   * Registra un guardián del espacio de trabajo. Si ya hay un tutorial activo
   * (el host montó DESPUÉS de arrancar el recorrido — p. ej. `graph-container`
   * cuando el paso 1 abre la tab Inmersión) se toma la foto en el acto.
   */
  registerWorkspaceGuard(key: string, guard: WorkspaceGuard): void {
    this.workspaceGuards.set(key, guard);
    if (this.active && !this.workspaceSnapshots.has(key)) {
      this.captureGuard(key, guard);
    }
  }

  unregisterWorkspaceGuard(key: string): void {
    this.workspaceGuards.delete(key);
    this.workspaceSnapshots.delete(key);
  }

  private captureGuard(key: string, guard: WorkspaceGuard): void {
    try {
      this.workspaceSnapshots.set(key, guard.snapshot());
    } catch {
      /* el guardián es best-effort; nunca bloquea el tutorial */
    }
  }

  private captureWorkspace(): void {
    this.workspaceSnapshots.clear();
    this.workspaceTouched = false;
    this.workspaceGuards.forEach((g, k) => this.captureGuard(k, g));
  }

  private restoreWorkspace(
    reason: 'pause' | 'finish' | 'stop',
    tutorialId: string,
  ): void {
    // Si el recorrido no movió nada (ni hostAction ni clickAnchorOnEnter), no
    // hay nada que devolver: tocar la vista al salir sería el ruido opuesto.
    if (this.workspaceTouched) {
      this.workspaceSnapshots.forEach((prev, key) => {
        const guard = this.workspaceGuards.get(key);
        if (!guard) return;
        try {
          guard.restore(prev, { reason, tutorialId });
        } catch {
          /* best-effort */
        }
      });
    }
    this.workspaceSnapshots.clear();
    this.workspaceTouched = false;
  }

  /** ¿El paso visible ahora mismo está esperando una acción real? (§17.9) */
  isAwaitingConfirm(): boolean {
    return this.awaitingConfirm;
  }

  /**
   * Un componente host llama esto desde el punto REAL donde ocurre una
   * acción (p. ej. `triggerSearch()`, `onNodeClick()`, `toggleDrawer()`).
   * Si hay un tutorial activo y el paso visible está "gated" esperando
   * exactamente este `actionId`, el paso se confirma y el motor avanza solo.
   * Si no hay match (otro paso, otro tutorial, o el paso no es gated), no
   * hace nada — es seguro llamarlo siempre, sin condicionarlo desde fuera.
   */
  notifyAction(actionId: string): void {
    if (!this.active || !this.awaitingConfirm) return;
    const current = this.steps[this.index];
    if (!current || current.confirmAction !== actionId) return;
    this.awaitingConfirm = false;
    // Breve respiro para que el usuario vea que "se registró" antes de saltar
    // al siguiente paso (evita el salto instantáneo, que se siente brusco).
    setTimeout(() => this.next(), 350);
  }

  /** Arranca un tutorial por id desde el principio (navega a routeMatch si aplica). */
  async start(id: string): Promise<void> {
    await this.startAt(id, 0);
  }

  /**
   * Retoma un tutorial pausado en el paso donde quedó (§19). Lo usa el FAB de
   * "Retomar" y el Centro de Tutoriales. Si no había paso guardado, arranca de 0.
   */
  async resume(id: string): Promise<void> {
    const step = this.progress.getPausedStep(id) ?? 0;
    await this.startAt(id, step);
  }

  /**
   * Núcleo de arranque: entra al tutorial en `initialIndex` (clamped). Al quedar
   * activo se limpia su marca de "pausado" — si el usuario vuelve a salir, se
   * re-guarda el nuevo paso.
   */
  async startAt(id: string, initialIndex: number): Promise<void> {
    const tutorial = this.getById(id);
    if (!tutorial) {
      console.warn(`[guided-tour] tutorial desconocido: ${id}`);
      return;
    }

    // Choke-point ÚNICO del apagado (§19.11): si el interruptor MAESTRO del admin
    // (organización) o la preferencia del usuario están en `false`, NINGÚN
    // tutorial se lanza — ni auto, ni contextual, ni encadenado, ni manual desde
    // el perfil. Garantiza que "desactivar" realmente apague todo, sin depender
    // de que cada llamador recuerde comprobarlo. Cierra cualquier tour activo.
    if (this.isEffectivelyOff()) {
      this.stop();
      return;
    }

    this.progress.clearPaused(id);
    this.active = tutorial;
    this.steps = tutorial.steps.filter((s) => this.stepAllowed(s));
    this.index = 0;
    // Foto del espacio de trabajo (§20 T6.3): si el recorrido acaba moviendo
    // pestañas/drawers, al salir se devuelve la vista como estaba.
    this.captureWorkspace();

    if (typeof tutorial.routeMatch === 'string') {
      const current = this.router.url.split('?')[0].split('#').pop() || '';
      if (!current.includes(tutorial.routeMatch)) {
        await this.router.navigateByUrl(tutorial.routeMatch).catch(() => false);
      }
    }
    const clamped = Math.min(
      Math.max(0, initialIndex),
      Math.max(0, this.steps.length - 1),
    );
    await this.enterStep(clamped, +1);
  }

  /**
   * Tutorial PAUSADO y relevante para la pantalla actual que el FAB de "Retomar"
   * debe ofrecer (§19). Elige el pausado más reciente cuyo `screenMatch`/
   * `routeMatch` esté contenido en `path`. null ⇒ no mostrar el FAB.
   */
  getResumable(path: string): { tutorial: Tutorial; step: number } | null {
    if (this.isEffectivelyOff()) return null;
    for (const id of this.progress.pausedIdsByRecency()) {
      const t = this.getById(id);
      if (!t || this.noAplicaAlUsuario(t)) continue;
      if (this.progress.getStatus(id) === 'skipped') continue; // desactivado
      // Se retoma desde su propia ventana (chip), no con el botón flotante.
      if (t.resumeInPlace) continue;
      if (!this.screenMatches(t, path)) continue;
      return { tutorial: t, step: this.progress.getPausedStep(id) ?? 0 };
    }
    return null;
  }

  /**
   * ¿Estamos EN la pantalla de este tutorial? La ruta sola no basta: los siete
   * recorridos de Inmersión comparten `casesnew/` pero viven en una pestaña
   * concreta, así que se exige además que su ancla de referencia esté montada.
   */
  private screenMatches(t: Tutorial, path: string): boolean {
    const frag =
      t.screenMatch ??
      (typeof t.routeMatch === 'string' ? t.routeMatch : undefined);
    if (!frag || !path.includes(frag)) return false;
    if (!t.requiresAnchor) return true;
    return !!document.querySelector(`[data-tour="${t.requiresAnchor}"]`);
  }

  async next(): Promise<void> {
    if (!this.active) return;
    // Gating SUAVE (§20 T3, antes §17.9): un paso con `confirmAction` sugiere la
    // acción real y la detecta (`notifyAction` adelanta el paso solo), pero YA NO
    // bloquea "Siguiente": nadie queda obligado a borrar un nodo, gastar
    // créditos o descargar un archivo para poder salir del recorrido.
    this.awaitingConfirm = false;
    if (this.index >= this.steps.length - 1) {
      await this.finish();
      return;
    }
    await this.enterStep(this.index + 1, +1);
  }

  async prev(): Promise<void> {
    if (!this.active || this.index <= 0) return;
    await this.enterStep(this.index - 1, -1);
  }

  /**
   * El usuario SALE del tutorial (botón "Salir", Escape, o un clic accidental
   * que cerró el panel — §19). No lo descarta: guarda el paso actual y lo deja
   * PAUSADO (sigue `notDone`). Aparece el FAB para retomarlo donde quedó. La
   * única forma de quitarlo es completarlo o desactivarlo en el perfil.
   */
  pause(): void {
    if (!this.active) return;
    const id = this.active.id;
    this.progress.setPausedStep(id, this.index);
    this.restoreWorkspace('pause', id);
    this.teardown();
  }

  /** @deprecated Alias histórico: hoy "salir" PAUSA (no descarta). Ver `pause()`. */
  skip(): void {
    this.pause();
  }

  /**
   * El usuario completó el tutorial. Ya NO arranca el encadenado (§20 T5): si
   * hay cadena, se OFRECE con la píldora — el usuario decide. El botón
   * secundario del último paso (`finishAndChain`) es el atajo para quien sí
   * quiere seguir de inmediato.
   */
  async finish(): Promise<void> {
    if (!this.active) return;
    const id = this.active.id;
    const chain = this.active.chainTo;
    const auto = this.active.chainAuto ? this.siguienteEnCadena(this.active) : null;
    this.progress.setStatus(id, 'done').subscribe();
    this.progress.clearPaused(id); // completado ⇒ ya no hay nada que retomar
    this.restoreWorkspace('finish', id);
    this.teardown();
    if (auto) {
      this.consumeOffer(auto);
      await this.start(auto);
    } else if (chain) {
      this.offer(chain);
    }
  }

  /**
   * Siguiente recorrido de la cadena que aplica al usuario y a la pantalla
   * (su pestaña existe). Salta los que no: p. ej. Comunicación en una unidad
   * que no la tiene.
   */
  private siguienteEnCadena(t: Tutorial): string | null {
    const vistos = new Set<string>([t.id]);
    let id = t.chainTo;
    while (id && !vistos.has(id)) {
      vistos.add(id);
      const n = this.getById(id);
      if (!n) return null;
      const anclaOk =
        !n.requiresAnchor ||
        !!document.querySelector(`[data-tour="${n.requiresAnchor}"]`);
      if (anclaOk && !this.noAplicaAlUsuario(n)) return n.id;
      id = n.chainTo;
    }
    return null;
  }

  /**
   * "Continuar con X" (botón SECUNDARIO del último paso, §20 T5): completa el
   * tutorial activo y arranca el encadenado en el acto, porque aquí el usuario
   * lo pidió explícitamente.
   */
  async finishAndChain(): Promise<void> {
    const chain = this.active?.chainTo;
    await this.finish();
    if (chain) {
      this.consumeOffer(chain); // ya no hace falta ofrecerlo: lo está iniciando
      await this.start(chain);
    }
  }

  /** Cierra sin registrar estado (p. ej. navegación externa). */
  stop(): void {
    if (this.active) this.restoreWorkspace('stop', this.active.id);
    this.teardown();
  }

  /** Reactiva un tutorial (vuelve a notDone) desde el Centro de Tutoriales. */
  resetTutorial(id: string): void {
    this.progress.clearPaused(id);
    // Reactivar desde el perfil es una petición explícita: olvida también el
    // enfriamiento de invitaciones (§20 T6.4) o la píldora no volvería en 7 días.
    this.progress.clearOffered(id);
    this.progress.setStatus(id, 'notDone').subscribe();
  }

  // ── internos ────────────────────────────────────────────────────────────────

  private stepAllowed(step: TutorialStep): boolean {
    if (!step.requiresPermission) return true;
    // Flag del rol de la sesión viva (p. ej. 'createCases' en casos.tablero).
    return this.progress.hasPermission(step.requiresPermission);
  }

  /**
   * Entra al paso `target`. Navega si el paso vive en otra ruta, corre la
   * hostAction, espera el ancla y — si no aparece — salta con gracia en la misma
   * dirección. `dir` = +1 (avanzando) o -1 (retrocediendo).
   */
  private async enterStep(target: number, dir: 1 | -1): Promise<void> {
    if (!this.active) return;
    if (target < 0) return;
    if (target > this.steps.length - 1) {
      await this.finish();
      return;
    }

    const step = this.steps[target];
    this.index = target;

    // 1) Navegación previa si el paso vive en otra vista.
    if (step.route) {
      const current = this.router.url;
      if (!current.includes(step.route)) {
        await this.router.navigateByUrl(step.route).catch(() => false);
      }
    }

    // 2) Acción del host (abrir panel/drawer/tab) — gratis y reversible.
    if (step.hostAction) {
      const fn = this.hostActions.get(step.hostAction);
      if (fn) {
        try {
          await fn();
          // El recorrido movió la vista del usuario ⇒ hay que devolverla al
          // salir (§20 T6.3).
          this.workspaceTouched = true;
        } catch {
          /* la acción es best-effort; no bloquea el tutorial */
        }
      }
    }

    // 3) Resolver ancla (con espera si monta tarde).
    let target$el: HTMLElement | null = null;
    if (step.anchor) {
      target$el = await this.resolveAnchor(
        step.anchor,
        step.waitForAnchorMs ?? 0,
      );
      if (!target$el) {
        // Degradación elegante: ancla ausente ⇒ saltar el paso.
        console.warn(
          `[guided-tour] ancla ausente, se salta el paso: ${step.anchor}`,
        );
        await this.enterStep(target + dir, dir);
        return;
      }
      if (step.clickAnchorOnEnter) {
        try {
          target$el.click();
          this.workspaceTouched = true; // p. ej. paseo por las pestañas del caso
        } catch {
          /* no-op */
        }
      }
      target$el.scrollIntoView({ block: 'center', inline: 'center' });
    }

    // 4) Armar el gating (§17.9): si el paso trae `confirmAction`, el motor
    // no avanza con "Siguiente" — espera a `notifyAction(confirmAction)`.
    this.awaitingConfirm = !!step.confirmAction;

    this.view$.next({
      tutorial: this.active,
      step,
      index: target,
      total: this.steps.length,
      target: target$el,
    });

    // 5) Vigila que el ancla no desaparezca (clic accidental que cierra el panel).
    this.startKnockoutWatch(target$el);
  }

  /** Arranca la vigilancia de knockout para el ancla del paso actual (§19). */
  private startKnockoutWatch(target: HTMLElement | null): void {
    this.stopKnockoutWatch();
    if (!target) return; // pasos centrados (sin ancla) no se vigilan
    this.watchedTarget = target;
    this.knockoutMisses = 0;
    this.knockoutTimer = setInterval(() => {
      if (!this.active || !this.watchedTarget) {
        this.stopKnockoutWatch();
        return;
      }
      if (document.body.contains(this.watchedTarget)) {
        this.knockoutMisses = 0;
        return;
      }
      this.knockoutMisses += 1;
      if (this.knockoutMisses >= 2) {
        // El ancla se fue y no volvió: el usuario salió sin querer del contexto
        // del paso. Pausamos recordando el paso → el FAB permite retomar.
        this.pause();
      }
    }, 700);
  }

  private stopKnockoutWatch(): void {
    if (this.knockoutTimer) {
      clearInterval(this.knockoutTimer);
      this.knockoutTimer = null;
    }
    this.watchedTarget = null;
    this.knockoutMisses = 0;
  }

  private resolveAnchor(
    anchor: string,
    timeoutMs: number,
  ): Promise<HTMLElement | null> {
    const sel = `[data-tour="${anchor}"]`;
    const found = document.querySelector<HTMLElement>(sel);
    if (found || timeoutMs <= 0) return Promise.resolve(found);

    return new Promise((resolve) => {
      const started = Date.now();
      const tick = () => {
        const el = document.querySelector<HTMLElement>(sel);
        if (el) return resolve(el);
        if (Date.now() - started >= timeoutMs) return resolve(null);
        setTimeout(tick, 120);
      };
      tick();
    });
  }

  private teardown(): void {
    this.stopKnockoutWatch();
    this.active = null;
    this.steps = [];
    this.index = 0;
    this.awaitingConfirm = false;
    this.view$.next(null);
  }
}
